const SCROLLABLE_OVERFLOW = new Set(["auto", "scroll", "overlay"]);

function isScrollableElement(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  return (
    SCROLLABLE_OVERFLOW.has(style.overflowY) ||
    SCROLLABLE_OVERFLOW.has(style.overflowX) ||
    SCROLLABLE_OVERFLOW.has(style.overflow)
  );
}

/**
 * Returns scrollable ancestor elements for a node, nearest first.
 * Skips non-scrollable parents (e.g. overflow: visible/hidden).
 */
export function getScrollableAncestors(element: HTMLElement): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent) {
    if (isScrollableElement(parent)) {
      ancestors.push(parent);
    }
    parent = parent.parentElement;
  }

  return ancestors;
}

/**
 * Subscribes to scroll/resize events that can move a fixed-position popover
 * relative to its anchor. Reposition callbacks should read fresh
 * getBoundingClientRect() values from the anchor.
 */
export function subscribeToPositionUpdates(
  anchor: HTMLElement,
  onUpdate: () => void,
): () => void {
  let rafId: number | null = null;
  const scheduledUpdate = () => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      onUpdate();
    });
  };

  onUpdate();

  const scrollTargets = new Set<EventTarget>();
  for (const ancestor of getScrollableAncestors(anchor)) {
    scrollTargets.add(ancestor);
  }
  scrollTargets.add(window);

  const scrollListenerOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
  };

  for (const target of scrollTargets) {
    target.addEventListener("scroll", scheduledUpdate, scrollListenerOptions);
  }

  window.addEventListener("resize", scheduledUpdate, { passive: true });

  const resizeObserver = new ResizeObserver(scheduledUpdate);
  resizeObserver.observe(anchor);

  const visualViewport = window.visualViewport;
  if (visualViewport) {
    visualViewport.addEventListener("resize", scheduledUpdate, {
      passive: true,
    });
    visualViewport.addEventListener("scroll", scheduledUpdate, {
      passive: true,
    });
  }

  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    for (const target of scrollTargets) {
      target.removeEventListener(
        "scroll",
        scheduledUpdate,
        scrollListenerOptions,
      );
    }
    window.removeEventListener("resize", scheduledUpdate);
    resizeObserver.disconnect();
    if (visualViewport) {
      visualViewport.removeEventListener("resize", scheduledUpdate);
      visualViewport.removeEventListener("scroll", scheduledUpdate);
    }
  };
}
